/**
 * AttribuerModal — panneau d'attribution d'une demande (Centre des demandes).
 *
 * Socle simple : personne + échéance + priorité.
 * Deux champs optionnels : un mot pour la personne, et le rattachement à une campagne.
 * Validation : une personne est obligatoire avant d'attribuer.
 *
 * Écrit via attribuerDemande() (première écriture V2). À la réussite, le onSnapshot
 * (poll 3 s) rafraîchit la liste tout seul ; on ferme simplement le panneau.
 */
import React, { useState, useMemo } from "react";
import { attribuerDemande } from "../hooks/useV1Write";

const PRIORITIES = ["Basse", "Normale", "Haute", "Urgente"];
const PRIORITY_COLOR = {
  "Basse":   "#64748B",
  "Normale": "#0F56B8",
  "Haute":   "#D97706",
  "Urgente": "#DC2626",
};
const ACCENT = "#0F56B8";

function userLabel(u) {
  if (!u) return "";
  return (
    u.name || u.displayName ||
    [u.prenom, u.nom].filter(Boolean).join(" ") ||
    [u.firstName, u.lastName].filter(Boolean).join(" ") ||
    u.email || ""
  );
}

function initials(name) {
  if (!name) return "?";
  const parts = String(name).split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0][0] || "?").toUpperCase();
}

function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/* Campagne « en cours » = publiée et aujourd'hui dans [dateStart, dateEnd] */
function campActive(c) {
  if (!c || !c.published) return false;
  const t = todayISO();
  if (c.dateStart && t < c.dateStart) return false;
  if (c.dateEnd && t > c.dateEnd) return false;
  return true;
}

export default function AttribuerModal({ request, task, users, campagnes, currentUser, appId, onClose }) {
  const [assigneeId, setAssigneeId] = useState(
    (task && Array.isArray(task.assignedTo) && task.assignedTo[0]) ||
    request.assigneeId ||
    (Array.isArray(request.assignedTo) && request.assignedTo[0]) ||
    ""
  );
  const [deadline, setDeadline] = useState(request.deadline || (task && task.deadline) || "");
  const [priority, setPriority] = useState((task && task.priority) || "Normale");
  const [note, setNote]         = useState("");
  const [campagneId, setCampagneId] = useState((task && task.campagneId) || request.campagneId || "");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  /* Équipe triée par nom. Campagnes publiées, les actives en tête. */
  const team = useMemo(
    () => (users || []).filter(Boolean).slice().sort((a, b) => userLabel(a).localeCompare(userLabel(b))),
    [users]
  );
  const campList = useMemo(() => {
    const pub = (campagnes || []).filter((c) => c && c.published !== false && c.name);
    return pub
      .map((c) => ({ id: c.id, name: c.name, active: campActive(c) }))
      .sort((a, b) => (b.active - a.active) || a.name.localeCompare(b.name));
  }, [campagnes]);

  async function handleSubmit() {
    if (!assigneeId) { setError("Choisis d'abord une personne."); return; }
    setSaving(true);
    setError(null);
    try {
      const assignee = team.find((u) => String(u.id) === String(assigneeId) || String(u.appId) === String(assigneeId));
      await attribuerDemande({
        request,
        assigneeId,
        assigneeName: userLabel(assignee),
        deadline: deadline || "",
        priority,
        note,
        campagneId: campagneId || "",
        actorId: appId || (currentUser && currentUser.id) || "",
      });
      onClose();
    } catch (e) {
      setError("L'attribution a échoué. Vérifie ta connexion et réessaie.");
      setSaving(false);
    }
  }

  const overlay = {
    position: "fixed", inset: 0, background: "rgba(26,26,28,.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20, zIndex: 1000,
  };
  const card = {
    width: "min(480px,100%)", maxHeight: "90vh", overflowY: "auto",
    background: "#fff", borderRadius: 14, padding: "20px 22px",
    boxShadow: "0 16px 48px rgba(0,0,0,.24)", fontFamily: "inherit",
  };
  const label = { fontSize: 13, fontWeight: 600, color: "var(--text, #1a1a1c)", margin: "0 0 8px" };
  const field = { marginBottom: 16 };
  const optTag = { fontWeight: 400, color: "var(--text-3, #94A3B8)", fontSize: 12 };
  const inputBase = {
    width: "100%", padding: "9px 12px", borderRadius: 9, border: "1.5px solid #E2E8F0",
    fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", background: "#F8FAFC", outline: "none",
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: "var(--text, #1a1a1c)" }}>Attribuer la demande</h2>
          <button onClick={onClose} aria-label="Fermer" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#94A3B8", lineHeight: 1 }}>×</button>
        </div>

        {/* Rappel de la demande */}
        <div style={{ background: "#F1F5F9", borderRadius: 9, padding: "12px 14px", marginBottom: 18 }}>
          <div style={{ fontSize: 11.5, color: "#64748B", marginBottom: 4 }}>
            {request.firstName ? `${request.firstName} · ` : ""}{request.email || "Demande"}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text, #1a1a1c)" }}>{request.subject || "–"}</div>
          {request.description && (
            <div style={{ fontSize: 12.5, color: "#475569", marginTop: 4, lineHeight: 1.5 }}>
              {request.description.slice(0, 160)}{request.description.length > 160 ? "…" : ""}
            </div>
          )}
        </div>

        {/* Confier à */}
        <div style={field}>
          <p style={label}>Confier à</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {team.map((u) => {
              const id = u.id != null ? u.id : u.appId;
              const name = userLabel(u);
              const sel = String(assigneeId) === String(id);
              return (
                <button
                  key={String(id)}
                  onClick={() => { setAssigneeId(id); setError(null); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "6px 12px 6px 6px", borderRadius: 999, cursor: "pointer",
                    fontSize: 13, fontFamily: "inherit",
                    border: sel ? `1.5px solid ${ACCENT}` : "1px solid #E2E8F0",
                    background: sel ? "#EFF6FF" : "#fff",
                    color: sel ? ACCENT : "var(--text, #1a1a1c)",
                  }}
                >
                  <span style={{
                    width: 26, height: 26, borderRadius: "50%", background: "#EFF6FF", color: ACCENT,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700,
                  }}>{initials(name)}</span>
                  <span>{name}{u.role ? <span style={{ color: "#94A3B8", fontWeight: 400 }}> · {u.role}</span> : null}</span>
                </button>
              );
            })}
          </div>
          {error && <p style={{ fontSize: 13, color: "#DC2626", margin: "8px 0 0" }}>{error}</p>}
        </div>

        {/* Échéance + Priorité */}
        <div style={{ display: "flex", gap: 14 }}>
          <div style={{ ...field, flex: 1 }}>
            <p style={label}>Échéance</p>
            <input type="date" value={deadline || ""} onChange={(e) => setDeadline(e.target.value)} style={inputBase} />
          </div>
          <div style={{ ...field, flex: 1.4 }}>
            <p style={label}>Priorité</p>
            <div style={{ display: "flex", gap: 6 }}>
              {PRIORITIES.map((p) => {
                const sel = priority === p;
                return (
                  <button
                    key={p}
                    onClick={() => setPriority(p)}
                    style={{
                      flex: 1, padding: "8px 0", borderRadius: 8, cursor: "pointer",
                      fontSize: 12.5, fontFamily: "inherit", fontWeight: sel ? 700 : 500,
                      border: sel ? `1.5px solid ${PRIORITY_COLOR[p]}` : "1px solid #E2E8F0",
                      background: sel ? "#fff" : "#F8FAFC",
                      color: sel ? PRIORITY_COLOR[p] : "#64748B",
                    }}
                  >{p}</button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Campagne (optionnel) */}
        <div style={field}>
          <p style={label}>Campagne <span style={optTag}>· optionnel</span></p>
          <select value={campagneId || ""} onChange={(e) => setCampagneId(e.target.value)} style={inputBase}>
            <option value="">Aucune</option>
            {campList.map((c) => (
              <option key={String(c.id)} value={String(c.id)}>
                {c.name}{c.active ? " — en cours" : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Mot pour la personne (optionnel) */}
        <div style={field}>
          <p style={label}>Mot pour la personne <span style={optTag}>· optionnel</span></p>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ex : reprends la charte graphique du club, affiche prête pour impression."
            style={{ ...inputBase, resize: "vertical" }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{ padding: "9px 16px", borderRadius: 9, border: "1px solid #CBD5E1", background: "transparent", fontSize: 14, cursor: "pointer", fontFamily: "inherit", color: "var(--text, #1a1a1c)" }}
          >Annuler</button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{ padding: "9px 20px", borderRadius: 9, border: "none", background: saving ? "#94A3B8" : ACCENT, color: "#fff", fontSize: 14, fontWeight: 700, cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}
          >{saving ? "Attribution…" : "Attribuer"}</button>
        </div>

      </div>
    </div>
  );
}
